import React from 'react';
import { Modal } from 'app-components';
import { FormattedMessage } from 'react-intl';
import { FieldValues } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
    COMPLETE_INTERNAL_COMMENT_COLS,
    COMPLETE_INTERNAL_COMMENT_MAX_LENGTH,
    COMPLETE_INTERNAL_COMMENT_ROWS,
    taskStatusTypes,
    ticketStatusTypes,
} from '../constants/ticketlist.constants';
import { useApiHandler } from '../hooks/useApiHandler';
import { useFormHandler } from '../hooks/useFormHandler';
import { ROOT_STATE } from '../../../../store/store.types';
import GIButton from '../../../../components/common/button/GIButton';
import { ICompleteTicketModal } from '../constants/ticketlist.interface';
import { StyledCharacterReamining, StyledInputContainer, StyledModal } from '../constants/ticketlist.styled';
import { updateTicketList } from '../constants/ticketlist.utils';
import { API_STATUS_CODES } from '../../../../constants/common.constants';

export const CompleteTicketModal = ({ onClose }: ICompleteTicketModal) => {
    const dispatch = useDispatch();
    const {
        tickets: { ticketListJson },
        modalData: { actTask, actProInstId, tkmTaskId, ticketIdVerify, taskHeader },
        modalFlags: { validateComments },
    } = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList);

    const { completeUserTaskAPI } = useApiHandler();
    const { completeTicketForm } = useFormHandler();
    const { handleSubmit, watch, register } = completeTicketForm;

    const onSubmit = async (formData: FieldValues) => {
        const { comments } = formData;

        const taskObject = {
            ActivitiTaskId: actTask,
            ProcessInstanceId: actProInstId,
            TaskId: tkmTaskId,
            TicketId: ticketIdVerify,
            ValidateComments: validateComments,
            Action: taskHeader,
            formPropertiesMap: {},
        };

        const payload = {
            taskObject: JSON.stringify(taskObject),
            comments: comments,
            commentLength: comments?.length,
            publicComments: '',
            csrftoken: window.csrf_token,
        };
        const { statusCode } = await completeUserTaskAPI(dispatch, payload);
        if (statusCode === API_STATUS_CODES.OK) {
            updateTicketList(dispatch, ticketListJson, 'encryptedId', ticketIdVerify, {
                taskStatus: taskStatusTypes.RESOLVED,
                status: ticketStatusTypes.RESOLVED,
            });
        }
        onClose();
    };

    return (
        <StyledModal>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal id="completeTicketModal" classNames="complete-ticket-modal" data-testid="complete-ticket-modal">
                    <Modal.Header
                        id="completeTicketModalHeader"
                        titleId="ticketList.modal.completeTicketModal.header"
                        data-testid="complete-ticket-modal-header"
                        onHide={onClose}
                    />
                    <Modal.Body data-testid="complete-ticket-modal-body">
                        <StyledInputContainer>
                            <label
                                id={'internalComment_label'}
                                data-testid={'internalComment_label'}
                                htmlFor="internalComment_textarea"
                            >
                                <FormattedMessage id="ticketList.modal.completeTicketModal.internalCommentsLabel" />
                            </label>
                            <textarea
                                id={'internalComment_textarea'}
                                className="internalComment"
                                aria-required={true}
                                data-testid={'internalComment_textarea'}
                                maxLength={COMPLETE_INTERNAL_COMMENT_MAX_LENGTH}
                                disabled={false}
                                cols={COMPLETE_INTERNAL_COMMENT_COLS}
                                rows={COMPLETE_INTERNAL_COMMENT_ROWS}
                                spellCheck={true}
                                {...register('comments')}
                            />
                            <StyledCharacterReamining id={'internalComment_charactersLeft'}>
                                <FormattedMessage
                                    id="ticketList.characterLeft"
                                    values={{
                                        charLeft: COMPLETE_INTERNAL_COMMENT_MAX_LENGTH - watch('comments').length,
                                    }}
                                />
                            </StyledCharacterReamining>
                        </StyledInputContainer>
                    </Modal.Body>
                    <Modal.Controls data-testid="complete-ticket-modal-controls">
                        <GIButton
                            id="completeTicketModalCancelButton"
                            onClickHandler={onClose}
                            variant="secondary"
                            labelId="button.cancel"
                        />
                        <GIButton
                            id="completeTicketModalSaveButton"
                            variant="primary"
                            type="submit"
                            labelId="ticketList.modal.completeTicketModal.button.taskComplete"
                        />
                    </Modal.Controls>
                </Modal>
            </form>
        </StyledModal>
    );
};
